import { Component, Inject, OnInit, Optional } from '@angular/core';

import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import {
    ChoicePossRulesGet,
    ChoicePossRulesItem,
    ChoicePossRulesService,
    ChosenProductItem,
    FurnitureFabricItem,
    ModelItem,
    ProductChoiceItem,
    ProductNameItem,
} from '@app/services/choice-poss-rules.service';
import { TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-choice-poss-rules',
    templateUrl: './choice-poss-rules.component.html',
    styleUrls: ['./choice-poss-rules.component.css'],
    standalone: false,
})
export class ChoicePossRulesComponent implements OnInit {
    public page?: ChoicePossRulesGet;

    public selectListProductChoices: ProductChoiceItem[] = [];
    public selectListModels: ModelItem[] = [];
    public selectListFurnitureFabrics: FurnitureFabricItem[] = [];
    public selectListChosenProducts: ChosenProductItem[] = [];
    public selectListProductNames: ProductNameItem[] = [];
    public itemsListRules: ChoicePossRulesItem[] = [];
    public refreshing = false;

    constructor(
        private choicePossRules: ChoicePossRulesService,
        private typeahead: TypeaheadService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchProducts = (params?: { value?: string }) => this.typeahead.lookUpProducts({
        prod_name: params?.value ?? '',
        webpage_name: 'choicepossrules',
        parm1: '0',
        parm2: '0',
        supplier_no: '0',
        search_only_this_supplier: '0',
        businessco_no: '0',
        onlyonstock: '0',
        model_id: this.page?.model_id ?? 'ALL',
    });

    public async onChoiceProductChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.choice_prod_id = value;
        this.page.furniturefabrics_id = 'ALL';
        this.page.prod_id_choosed = '';
        await this.loadChoiceProductDetails();
        await this.loadRules();
    }

    public async onModelChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.model_id = value;
        this.clearProduct();
        await this.loadModelDetails();
        await this.loadRules();
    }

    public async onFurnitureFabricsChanged(value: string): Promise<void> {
        if (!this.page || !value) return;

        this.page.furniturefabrics_id = value;
        if (value === 'ALL') this.page.prod_id_choosed = '';
        await this.loadChosenProducts();
        await this.loadRules();
    }

    public async onChosenProductChanged(value: string): Promise<void> {
        if (!this.page) return;

        this.page.prod_id_choosed = value;
        await this.loadRules();
    }

    public async onProductNameChanged(value: string): Promise<void> {
        if (!this.page) return;

        this.page.prod_id = value;
        this.page.prod_name = this.selectListProductNames.find((item) => item.prod_id === value)?.prod_name ?? '';
        await this.loadRules();
    }

    public async onProductSearchSelected(item?: { item_id: string | number; item_name?: string }): Promise<void> {
        if (!this.page) return;

        this.page.prod_id = `${item?.item_id ?? ''}`;
        this.page.prod_name = item?.item_name ?? '';
        await this.loadRules();
    }

    public async onProductSearchCleared(): Promise<void> {
        this.clearProduct();
        await this.loadRules();
    }

    public async refresh(): Promise<void> {
        this.refreshing = true;
        await this.loadRules();
    }

    private async init(): Promise<void> {
        const argtype = this.navigator?.params['argtype'];
        const argvalue = this.navigator?.params['argvalue'];
        if (argtype === undefined || argvalue === undefined) return;

        await Promise.all([this.loadProductChoices(), this.loadModels()]);
        this.page = await this.choicePossRules.getRulesPage({ argtype: `${argtype}`, argvalue: `${argvalue}` });
        await Promise.all([this.loadChoiceProductDetails(), this.loadModelDetails()]);
        await this.loadRules();
    }

    private async loadProductChoices(): Promise<void> {
        this.selectListProductChoices = await this.choicePossRules.listProductChoices();
    }

    private async loadModels(): Promise<void> {
        this.selectListModels = await this.choicePossRules.listModels({ add_all: '1', show_inactive: '0' });
    }

    private async loadChoiceProductDetails(): Promise<void> {
        if (!this.page?.choice_prod_id?.trim()) return;

        this.page.nbr_of_furniturefabrics = (await this.choicePossRules.changeChoiceProductId({ choice_prod_id: this.page.choice_prod_id })).nbr_of_furniturefabrics;
        await Promise.all([this.loadFurnitureFabrics(), this.loadChosenProducts()]);
    }

    private async loadModelDetails(): Promise<void> {
        if (!this.page?.model_id?.trim()) return;

        this.page.nbr_of_products = (await this.choicePossRules.changeModelId(this.page)).nbr_of_products;
        await this.loadProductNames();
    }

    private async loadFurnitureFabrics(): Promise<void> {
        if (!this.page?.choice_prod_id?.trim() || this.page.nbr_of_furniturefabrics <= 0) return;

        this.selectListFurnitureFabrics = await this.choicePossRules.listFurnitureFabrics({ add_all: '1', choice_prod_id: this.page.choice_prod_id });
    }

    private async loadChosenProducts(): Promise<void> {
        if (!this.page?.choice_prod_id || !this.page.furniturefabrics_id) return;

        this.selectListChosenProducts = await this.choicePossRules.listChosenProducts(this.page);
    }

    private async loadProductNames(): Promise<void> {
        if (!this.page?.model_id?.trim() || this.page.nbr_of_products >= 200) return;

        this.selectListProductNames = await this.choicePossRules.listProductNames(this.page);
    }

    private async loadRules(): Promise<void> {
        try {
            if (!this.page?.choice_prod_id || this.page.choice_prod_id === '0' || !this.page.model_id || this.page.model_id === '0') {
                this.itemsListRules = [];
                return;
            }

            this.itemsListRules = await this.choicePossRules.listRules(this.page);
        } finally {
            this.refreshing = false;
        }
    }

    public async goToProduct(prodId?: string): Promise<void> {
        if (!prodId?.trim()) return;

        const product = await this.choicePossRules.getProductKeyno({ prod_id: prodId });
        if (product?.prod_keyno) this.navigator?.go({ to: 'product', parms: { prod_id: product.prod_keyno } });
    }

    private clearProduct(): void {
        if (!this.page) return;

        this.page.prod_id = '';
        this.page.prod_name = '';
    }

    ngOnInit(): void {
        void this.init();
    }
}
